import { create } from 'zustand';
import type { SyncStatus } from './sync/provider.js';

export type View =
  | { kind: 'empty' }
  | { kind: 'note'; id: string; heading?: string; blockId?: string }
  | { kind: 'graph' }
  | { kind: 'canvas'; id: string }
  | { kind: 'base'; id?: string };

export type AuthState = 'loading' | 'setup' | 'login' | 'ready';
export type EditorMode = 'live' | 'source' | 'reading';
export type SidebarTab = 'files' | 'search' | 'tags';

interface AppStore {
  authState: AuthState;
  username?: string;
  setAuth(state: AuthState, username?: string): void;

  /** Open editor tabs (at least 1). `view` mirrors the active tab so every
   * existing `s.view` consumer keeps working unchanged. */
  tabs: View[];
  activeTab: number;
  view: View;
  /** Navigate: replace the active tab, or (opts.newTab) open a new tab. */
  openView(view: View, opts?: { newTab?: boolean }): void;
  openNote(id: string, opts?: { heading?: string; blockId?: string; newTab?: boolean }): void;
  /** Applied on popstate — replaces the active tab, no history push. */
  restoreView(view: View): void;
  /** Switch to tab i (records history). */
  setActiveTab(i: number): void;
  /** Close tab i; closing the last tab resets to a single Home tab. */
  closeTab(i: number): void;
  /** Open a fresh Home tab. */
  newTab(): void;
  /** Boot: restore persisted tabs, honoring a deep-linked URL view. */
  bootTabs(urlView: View): void;

  editorMode: EditorMode;
  setEditorMode(m: EditorMode): void;

  sidebarTab: SidebarTab;
  setSidebarTab(t: SidebarTab): void;
  /** Mobile drawer visibility (desktop sidebar is always visible). */
  sidebarOpen: boolean;
  setSidebarOpen(open: boolean): void;
  rightOpen: boolean;
  setRightOpen(open: boolean): void;

  quickSwitcherOpen: boolean;
  setQuickSwitcherOpen(open: boolean): void;

  assistantOpen: boolean;
  setAssistantOpen(open: boolean): void;

  /** Pinned/starred note ids (persisted to localStorage). */
  pinnedIds: string[];
  togglePin(id: string): void;

  /** Id of the note currently being exported to PDF (drives the print portal). */
  printNoteId: string | null;
  setPrintNoteId(id: string | null): void;

  syncStatus: SyncStatus;
  setSyncStatus(s: SyncStatus): void;

  /** On-screen keyboard visible (iOS) — hides chrome, shows shortcut row. */
  keyboardOpen: boolean;
  setKeyboardOpen(open: boolean): void;
}

const viewToPath = (v: View): string => {
  switch (v.kind) {
    case 'note':
      return `/n/${v.id}`;
    case 'graph':
      return '/graph';
    case 'canvas':
      return `/c/${v.id}`;
    case 'base':
      return v.id ? `/b/${v.id}` : '/b';
    default:
      return '/';
  }
};

export const pathToView = (path: string): View => {
  const m = path.match(/^\/(n|c|b)\/([^/]+)$/);
  if (m) {
    if (m[1] === 'n') return { kind: 'note', id: m[2] };
    if (m[1] === 'c') return { kind: 'canvas', id: m[2] };
    return { kind: 'base', id: m[2] };
  }
  if (path === '/graph') return { kind: 'graph' };
  if (path === '/b') return { kind: 'base' };
  return { kind: 'empty' };
};

const sameView = (a: View, b: View) => JSON.stringify(a) === JSON.stringify(b);
const clampIdx = (i: number, len: number) => Math.max(0, Math.min(i, len - 1));

const TABS_KEY = 'rbv.tabs';
const MAX_TABS = 20;
function saveTabs(tabs: View[], activeTab: number): void {
  try {
    localStorage.setItem(TABS_KEY, JSON.stringify({ tabs: tabs.slice(0, MAX_TABS), activeTab }));
  } catch {
    /* storage full / disabled — session-restore is best-effort */
  }
}
function loadTabs(): { tabs: View[]; activeTab: number } | null {
  try {
    const raw = localStorage.getItem(TABS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { tabs?: View[]; activeTab?: number };
    if (!Array.isArray(p.tabs) || !p.tabs.length) return null;
    return { tabs: p.tabs.slice(0, MAX_TABS), activeTab: clampIdx(p.activeTab ?? 0, p.tabs.length) };
  } catch {
    return null;
  }
}

const PINS_KEY = 'rbv.pinnedNotes';
function loadPins(): string[] {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export const useApp = create<AppStore>((set, get) => ({
  authState: 'loading',
  setAuth: (authState, username) => set({ authState, username }),

  tabs: [{ kind: 'empty' }],
  activeTab: 0,
  view: { kind: 'empty' },
  openView: (view, opts) => {
    const s = get();
    if (opts?.newTab) {
      const tabs = [...s.tabs, view].slice(-MAX_TABS);
      const activeTab = tabs.length - 1;
      history.pushState(null, '', viewToPath(view));
      set({ tabs, activeTab, view, sidebarOpen: false, quickSwitcherOpen: false });
      saveTabs(tabs, activeTab);
      return;
    }
    if (sameView(s.view, view)) {
      set({ sidebarOpen: false, quickSwitcherOpen: false });
      return;
    }
    const tabs = s.tabs.slice();
    tabs[s.activeTab] = view;
    history.pushState(null, '', viewToPath(view));
    set({ tabs, view, sidebarOpen: false, quickSwitcherOpen: false });
    saveTabs(tabs, s.activeTab);
  },
  openNote: (id, opts) =>
    get().openView(
      { kind: 'note', id, heading: opts?.heading, blockId: opts?.blockId },
      { newTab: opts?.newTab },
    ),
  restoreView: (view) => {
    const s = get();
    const tabs = s.tabs.slice();
    tabs[s.activeTab] = view;
    set({ tabs, view });
    saveTabs(tabs, s.activeTab);
  },
  setActiveTab: (i) => {
    const s = get();
    if (i < 0 || i >= s.tabs.length || i === s.activeTab) return;
    const view = s.tabs[i];
    history.pushState(null, '', viewToPath(view));
    set({ activeTab: i, view, sidebarOpen: false });
    saveTabs(s.tabs, i);
  },
  closeTab: (i) => {
    const s = get();
    if (i < 0 || i >= s.tabs.length) return;
    if (s.tabs.length <= 1) {
      const tabs: View[] = [{ kind: 'empty' }];
      history.pushState(null, '', '/');
      set({ tabs, activeTab: 0, view: tabs[0] });
      saveTabs(tabs, 0);
      return;
    }
    const tabs = s.tabs.filter((_, idx) => idx !== i);
    let activeTab = s.activeTab;
    if (i < activeTab) activeTab -= 1;
    else if (i === activeTab) activeTab = clampIdx(activeTab, tabs.length);
    const view = tabs[activeTab];
    history.pushState(null, '', viewToPath(view));
    set({ tabs, activeTab, view });
    saveTabs(tabs, activeTab);
  },
  newTab: () => get().openView({ kind: 'empty' }, { newTab: true }),
  bootTabs: (urlView) => {
    const persisted = loadTabs();
    let tabs: View[] = persisted?.tabs.length ? persisted.tabs : [{ kind: 'empty' }];
    let activeTab = clampIdx(persisted?.activeTab ?? 0, tabs.length);
    if (urlView.kind !== 'empty') {
      const existing = tabs.findIndex((t) => sameView(t, urlView));
      if (existing >= 0) activeTab = existing;
      else {
        tabs = tabs.slice();
        tabs[activeTab] = urlView;
      }
    }
    set({ tabs, activeTab, view: tabs[activeTab] });
    saveTabs(tabs, activeTab);
  },

  editorMode: 'live',
  setEditorMode: (editorMode) => set({ editorMode }),

  sidebarTab: 'files',
  setSidebarTab: (sidebarTab) => set({ sidebarTab, sidebarOpen: true }),
  sidebarOpen: false,
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  rightOpen: false,
  setRightOpen: (rightOpen) => set({ rightOpen }),

  quickSwitcherOpen: false,
  setQuickSwitcherOpen: (quickSwitcherOpen) => set({ quickSwitcherOpen }),

  assistantOpen: false,
  setAssistantOpen: (assistantOpen) => set({ assistantOpen }),

  pinnedIds: loadPins(),
  togglePin: (id) => {
    const cur = get().pinnedIds;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [id, ...cur];
    try {
      localStorage.setItem(PINS_KEY, JSON.stringify(next));
    } catch {
      /* best-effort */
    }
    set({ pinnedIds: next });
  },

  printNoteId: null,
  setPrintNoteId: (printNoteId) => set({ printNoteId }),

  syncStatus: 'connecting',
  setSyncStatus: (syncStatus) => set({ syncStatus }),

  keyboardOpen: false,
  setKeyboardOpen: (keyboardOpen) => set({ keyboardOpen }),
}));
